import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  createApiKey, createComputer, createGroup, createRepo, deleteApiKey, deleteComputer, deleteGroup, deleteRepo,
  getOrchestratorStatus, getWorkflowYaml, listApiKeys, listComputers, listGroups, listRepos, pauseRunner, resumeRunner, syncProjects,
  updateComputer, updateGroup, updateRepo,
  type QaApiKey, type QaComputer, type QaGitHubRepo, type QaOrchestratorStatus, type QaRunnerGroup, type QaSyncResult,
} from "../api/qaOrchestrator";
import { IconBack, IconPlus, IconTrash, IconX } from "./admin/icons";
import { relativeTime } from "./admin/memberModel";
import { usePolling } from "../hooks/usePolling";
import "./admin/members.css";
import QaActivitySection from "./admin/QaActivitySection";
import QaConnectionSection from "./admin/QaConnectionSection";
import QaRepoSettings from "./admin/QaRepoSettings";
import QaNotificationsSection from "./admin/QaNotificationsSection";
import QaRetentionSection from "./admin/QaRetentionSection";
import { DRAFT_GROUP, NeedsYouList, StatePill, TargetEditor, useAction } from "./admin/qaShared";
import "./admin/qa-orchestrator.css";

type Props = { onClose: () => void; onOpenUsers: () => void; onOpenUsage: () => void; onOpenErrors: () => void };
type Section = "runners" | "groups" | "repos" | "access" | "activity" | "settings" | "notifications" | "ci" | "connection";

const SECTIONS: { id: Section; label: string }[] = [
  { id: "runners", label: "Runners" },
  { id: "groups", label: "Runner groups" },
  { id: "repos", label: "Repositories" },
  { id: "access", label: "API keys" },
  { id: "activity", label: "Activity" },
  { id: "settings", label: "Storage" },
  { id: "notifications", label: "Notifications" },
  { id: "ci", label: "CI setup" },
  { id: "connection", label: "Connection" },
];

// ---------------------------------------------------------------- runners

function RunnersSection({ computers, groups, reload, setError }: { computers: QaComputer[]; groups: QaRunnerGroup[]; reload: () => Promise<void>; setError: (e: string | null) => void }) {
  const { busy, run, confirmThen, dialog } = useAction(reload, setError);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", baseUrl: "", token: "", notes: "", groups: [] as string[] });
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState({ name: "", baseUrl: "", token: "", notes: "" });

  const add = async () => {
    const ok = await run(async () => {
      const { computer } = await createComputer({ name: form.name.trim(), baseUrl: form.baseUrl.trim(), token: form.token, ...(form.notes.trim() ? { notes: form.notes.trim() } : {}) });
      for (const g of form.groups) {
        const group = groups.find((x) => x.id === g);
        if (group) await updateGroup(g, { computers: [...group.computers, computer.id] });
      }
    });
    if (ok) {
      setAdding(false);
      setForm({ name: "", baseUrl: "", token: "", notes: "", groups: [] });
    }
  };

  const save = async (c: QaComputer) => {
    const ok = await run(() => updateComputer(c.id, {
      ...(edit.name.trim() && edit.name.trim() !== c.name ? { name: edit.name.trim() } : {}),
      ...(edit.baseUrl.trim() && edit.baseUrl.trim() !== c.baseUrl ? { baseUrl: edit.baseUrl.trim() } : {}),
      ...(edit.token ? { token: edit.token } : {}),
      ...((edit.notes.trim() || null) !== c.notes ? { notes: edit.notes.trim() || null } : {}),
    }));
    if (ok) setEditing(null);
  };

  return (
    <section className="qo-section">
      <div className="qo-section-head">
        <div>
          <h3>Runners</h3>
          <p>Windows machines with the qa-agent runner installed. Each runs one test at a time and waits while someone is using its desktop; every plan goes to the least busy runner it is allowed on. A disabled runner gets no new work; a paused one holds its own queue.</p>
        </div>
        <button className="btn btn-accent" onClick={() => setAdding((v) => !v)}><IconPlus /> Add runner</button>
      </div>

      {adding && (
        <div className="qo-card qo-form">
          <p className="qo-hint">
            On the machine: install the runner (<code>packages/runner-agent/scripts/install-service.ps1</code> in the qa-agent repo), which prints its token,
            and expose it over HTTPS (e.g. a Cloudflare tunnel). Then:
          </p>
          <div className="qo-grid">
            <label>Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="win-1" /></label>
            <label>URL<input value={form.baseUrl} onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} placeholder="https://qa-runner.example.org" /></label>
            <label>Token<input type="password" value={form.token} onChange={(e) => setForm({ ...form, token: e.target.value })} autoComplete="off" /></label>
            <label>Notes <small>optional</small><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Where it is, who uses it" /></label>
          </div>
          {groups.length > 0 && (
            <div className="qo-checks">
              <span>Groups <small>optional</small></span>
              {groups.map((g) => (
                <label key={g.id}>
                  <input type="checkbox" checked={form.groups.includes(g.id)} onChange={(e) => setForm({ ...form, groups: e.target.checked ? [...form.groups, g.id] : form.groups.filter((x) => x !== g.id) })} />
                  {g.name}
                </label>
              ))}
            </div>
          )}
          <div className="qo-actions">
            <button className="btn btn-accent" disabled={busy || !form.name.trim() || !form.baseUrl.trim() || !form.token} onClick={() => void add()}>Add</button>
            <button className="btn btn-ghost" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      {computers.length === 0 ? (
        <div className="members-empty">No runners yet. Add the first one to run tests.</div>
      ) : (
        <div className="qo-table-wrap">
          <table className="qo-table">
            <thead>
              <tr><th>Runner</th><th>State</th><th>Groups</th><th>Last seen</th><th /></tr>
            </thead>
            <tbody>
              {computers.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.name}</strong>
                    <small>{c.baseUrl}</small>
                    {c.notes && <small>{c.notes}</small>}
                    {/* A pause shows in the state column, as maintenance with its reason. */}
                    <NeedsYouList items={c.needsYou?.filter((i) => i.id !== "paused")} />
                    {editing === c.id && (
                      <div className="qo-inline-form">
                        <input aria-label="Name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Name" />
                        <input aria-label="URL" value={edit.baseUrl} onChange={(e) => setEdit({ ...edit, baseUrl: e.target.value })} placeholder="URL" />
                        <input type="password" value={edit.token} onChange={(e) => setEdit({ ...edit, token: e.target.value })} placeholder="New token (leave empty to keep)" autoComplete="off" />
                        <input aria-label="Notes" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} placeholder="Notes" />
                        <div className="qo-actions">
                          <button className="btn btn-accent" disabled={busy} onClick={() => void save(c)}>Save</button>
                          <button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    <StatePill state={c.state} />
                    {c.pauseReason && <small>{c.pauseReason}{c.pausedSince ? ` · ${relativeTime(c.pausedSince)}` : ""}</small>}
                    {c.state === "unavailable" && c.unavailableReason && !c.needsYou?.length && <small>{c.unavailableReason}</small>}
                    {c.state === "offline" && c.lastError && <small className="qo-error-text">{c.lastError}</small>}
                    {(c.queued ?? 0) + (c.running ?? 0) > 0 && <small>{c.running ?? 0} running · {c.queued ?? 0} queued</small>}
                  </td>
                  <td>{c.groups.length ? c.groups.map((g) => <span key={g} className="qo-tag qo-tag-group">{g}</span>) : <span className="qo-muted">—</span>}</td>
                  <td>{c.lastSeenAt ? relativeTime(c.lastSeenAt) : <span className="qo-muted">never</span>}</td>
                  <td className="qo-row-actions">
                    {c.enabled && c.state !== "offline" && (c.state === "paused" || c.paused ? (
                      <button className="btn btn-ghost" disabled={busy} onClick={() => void confirmThen({ title: `Resume ${c.name}?`, body: "It starts taking tests again. Make sure nobody is using its desktop.", confirmLabel: "Resume", danger: false }, () => resumeRunner(c.id))}>Resume</button>
                    ) : (
                      <button className="btn btn-ghost" disabled={busy} onClick={() => void confirmThen({ title: `Pause ${c.name}?`, body: "The runner's emergency stop: it closes every Minecraft client and Bedrock server on that machine at once, cancels the test it is running, and runs nothing until it is resumed (here or at the machine). It resumes by itself after an hour with nobody using the desktop.", confirmLabel: "Pause" }, () => pauseRunner(c.id))}>Pause</button>
                    ))}
                    <button className="btn btn-ghost" disabled={busy} onClick={() => void run(() => updateComputer(c.id, { enabled: !c.enabled }))}>{c.enabled ? "Disable" : "Enable"}</button>
                    <button className="btn btn-ghost" onClick={() => { setEditing(c.id); setEdit({ name: c.name, baseUrl: c.baseUrl, token: "", notes: c.notes ?? "" }); }}>Edit</button>
                    <button className="btn btn-ghost" aria-label={`Delete ${c.name}`} disabled={busy} onClick={() => void confirmThen({ title: `Delete runner ${c.name}?`, body: "Its runs stay in the history. Groups lose it as a member.", confirmLabel: "Delete" }, () => deleteComputer(c.id))}><IconTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog}
    </section>
  );
}

// ---------------------------------------------------------------- groups

function GroupsSection({ computers, groups, reload, setError }: { computers: QaComputer[]; groups: QaRunnerGroup[]; reload: () => Promise<void>; setError: (e: string | null) => void }) {
  const { busy, run, confirmThen, dialog } = useAction(reload, setError);
  const [form, setForm] = useState({ id: "", name: "", notes: "", computers: [] as string[] });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<{ id: string; name: string; notes: string } | null>(null);
  const idOk = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(form.id);

  const toggle = (g: QaRunnerGroup, computerId: string, on: boolean) =>
    run(() => updateGroup(g.id, { computers: on ? [...g.computers, computerId] : g.computers.filter((c) => c !== computerId) }));

  return (
    <section className="qo-section">
      <div className="qo-section-head">
        <div>
          <h3>Runner groups</h3>
          <p>A named set of runners. A repository or a workflow (<code>QA_RUNNER_GROUP</code>) names the group, and which machines that means is changed here, in one place.</p>
        </div>
        <button className="btn btn-accent" onClick={() => setAdding((v) => !v)}><IconPlus /> New group</button>
      </div>

      {adding && (
        <div className="qo-card qo-form">
          <div className="qo-grid">
            <label>ID <small>letters, digits, - and _</small><input value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} placeholder="nightly" /></label>
            <label>Name <small>optional</small><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nightly machines" /></label>
            <label>Notes <small>optional</small><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
          </div>
          <div className="qo-checks">
            <span>Runners</span>
            {computers.map((c) => (
              <label key={c.id}>
                <input type="checkbox" checked={form.computers.includes(c.id)} onChange={(e) => setForm({ ...form, computers: e.target.checked ? [...form.computers, c.id] : form.computers.filter((x) => x !== c.id) })} />
                {c.name}
              </label>
            ))}
          </div>
          <div className="qo-actions">
            <button className="btn btn-accent" disabled={busy || !idOk} onClick={async () => {
              if (await run(() => createGroup({ id: form.id, ...(form.name.trim() ? { name: form.name.trim() } : {}), ...(form.notes.trim() ? { notes: form.notes.trim() } : {}), computers: form.computers }))) {
                setAdding(false);
                setForm({ id: "", name: "", notes: "", computers: [] });
              }
            }}>Create</button>
            <button className="btn btn-ghost" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      {groups.length === 0 ? (
        <div className="members-empty">No groups. Without one, tests go to any free runner, which is fine for a single machine.</div>
      ) : (
        <div className="qo-cards">
          {groups.map((g) => (
            <div key={g.id} className="qo-card" data-group={g.id}>
              <div className="qo-card-head">
                <div>
                  <strong>{g.name}</strong> <code className="qo-id">{g.id}</code>
                  {g.id === DRAFT_GROUP && <span className="qo-tag">built in</span>}
                </div>
                <div className="qo-row-actions">
                  <button className="btn btn-ghost" onClick={() => setEditing(editing?.id === g.id ? null : { id: g.id, name: g.name, notes: g.notes ?? "" })}>Edit</button>
                  {g.id !== DRAFT_GROUP && (
                    <button className="btn btn-ghost" aria-label={`Delete group ${g.id}`} disabled={busy} onClick={() => void confirmThen({ title: `Delete group ${g.id}?`, body: "Repositories and workflows naming it stop matching any runner.", confirmLabel: "Delete" }, () => deleteGroup(g.id))}><IconTrash /></button>
                  )}
                </div>
              </div>
              {g.id === DRAFT_GROUP ? (
                <p className="qo-hint qo-hint-tight">The runners the MCP server's drafts and debug sessions use (the first one up, by name). It can't be deleted; pick its runners here.</p>
              ) : g.notes && <p className="qo-hint qo-hint-tight">{g.notes}</p>}
              {editing?.id === g.id && (
                <div className="qo-inline-form">
                  <input aria-label="Group name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Name" />
                  <input aria-label="Group notes" value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="Notes" />
                  <div className="qo-actions">
                    <button className="btn btn-accent" disabled={busy || !editing.name.trim()} onClick={async () => {
                      if (await run(() => updateGroup(g.id, { name: editing.name.trim(), notes: editing.notes.trim() || null }))) setEditing(null);
                    }}>Save</button>
                    <button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </div>
              )}
              <div className="qo-checks">
                {computers.map((c) => (
                  <label key={c.id}>
                    <input type="checkbox" disabled={busy} checked={g.computers.includes(c.id)} onChange={(e) => void toggle(g, c.id, e.target.checked)} />
                    {c.name} <StatePill state={c.state} />
                  </label>
                ))}
                {computers.length === 0 && <span className="qo-muted">Add runners first.</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {dialog}
    </section>
  );
}

// ---------------------------------------------------------------- repos

function ReposSection({ repos, computers, groups, projects, reload, setError }: {
  repos: QaGitHubRepo[]; computers: QaComputer[]; groups: QaRunnerGroup[]; projects: NonNullable<QaOrchestratorStatus["projects"]>;
  reload: () => Promise<void>; setError: (e: string | null) => void;
}) {
  const { busy, run, confirmThen, dialog } = useAction(reload, setError);
  const [scope, setScope] = useState("");
  const [sync, setSync] = useState<QaSyncResult[] | null>(null);
  const [settingsOf, setSettingsOf] = useState<string | null>(null);
  const projectOf = useMemo(() => {
    const byRepo = new Map<string, string>();
    for (const p of projects) if (p.repo) byRepo.set(p.repo.toLowerCase(), p.name);
    return (s: string) => byRepo.get(s.toLowerCase());
  }, [projects]);

  return (
    <section className="qo-section">
      <div className="qo-section-head">
        <div>
          <h3>Repositories</h3>
          <p>
            Where each repository's tests run, when its workflow doesn't say. Every add-on project's repository is added automatically; a scope can also be a
            whole owner (<code>thunderay</code>) or <code>*</code>.
          </p>
        </div>
        <button className="btn" disabled={busy} onClick={() => void run(async () => setSync(await syncProjects()))}>Add project repositories now</button>
      </div>

      {sync && (
        <div className="qo-card qo-note">
          <button className="qo-close" onClick={() => setSync(null)} aria-label="Dismiss"><IconX /></button>
          {sync.length === 0 ? "No add-on projects." : sync.map((r) => (
            <div key={r.project}>
              <strong>{r.project}</strong>: {r.status === "created" ? `added ${r.repo}` : r.status === "exists" ? `${r.repo} already there` : r.status === "no_repo" ? "no GitHub repository" : `failed: ${r.error}`}
            </div>
          ))}
        </div>
      )}

      <div className="qo-table-wrap">
        <table className="qo-table">
          <thead>
            <tr><th>Repository</th><th>Runs on</th><th /></tr>
          </thead>
          <tbody>
            {repos.map((r) => (
              <Fragment key={r.id}>
              <tr>
                <td>
                  <strong>{r.scope}</strong>
                  {projectOf(r.scope) && <small>{projectOf(r.scope)}</small>}
                  <small>
                    {r.plans?.length ? `plans: ${r.plans.join(", ")}` : "every plan"} · {r.report === "check" ? "check run" : "commit status"} “{r.context}”
                    {r.comment ? " · PR comment" : ""}{r.allowForks ? " · forks too" : ""}
                  </small>
                  {r.notes && <small>{r.notes}</small>}
                </td>
                <td><TargetEditor value={r.target} computers={computers} groups={groups} disabled={busy} onChange={(target) => void run(() => updateRepo(r.id, { target }))} /></td>
                <td className="qo-row-actions">
                  <button className="btn btn-ghost" aria-expanded={settingsOf === r.id} onClick={() => setSettingsOf(settingsOf === r.id ? null : r.id)}>Settings</button>
                  <button className="btn btn-ghost" aria-label={`Remove ${r.scope}`} disabled={busy} onClick={() => void confirmThen({ title: `Remove ${r.scope}?`, body: "Its tests then run on any runner, with the defaults.", confirmLabel: "Remove" }, () => deleteRepo(r.id))}><IconTrash /></button>
                </td>
              </tr>
              {settingsOf === r.id && (
                <tr className="qo-expanded">
                  <td colSpan={3}>
                    <QaRepoSettings repo={r} busy={busy} onCancel={() => setSettingsOf(null)} onSave={async (patch) => { if (await run(() => updateRepo(r.id, patch))) setSettingsOf(null); }} />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="qo-add-row">
        <input value={scope} onChange={(e) => setScope(e.target.value)} placeholder="owner/repo, owner or *" />
        <button className="btn" disabled={busy || !scope.trim()} onClick={async () => { if (await run(() => createRepo({ scope: scope.trim() }))) setScope(""); }}><IconPlus /> Add</button>
      </div>
      {dialog}
    </section>
  );
}

// ---------------------------------------------------------------- access

function AccessSection({ keys, reload, setError }: { keys: QaApiKey[]; reload: () => Promise<void>; setError: (e: string | null) => void }) {
  const { busy, run, confirmThen, dialog } = useAction(reload, setError);
  const [keyName, setKeyName] = useState("");
  const [newKey, setNewKey] = useState<string | null>(null);

  return (
    <section className="qo-section">
      <div className="qo-section-head">
        <div>
          <h3>API keys</h3>
          <p>
            For CI (the <code>QA_ORCHESTRATOR_KEY</code> secret) and other tools. A key is shown once, when it is created. Private repositories are read with
            this workspace's GitHub App (see Connection).
          </p>
        </div>
      </div>
      {newKey && (
        <div className="qo-card qo-note">
          <button className="qo-close" onClick={() => setNewKey(null)} aria-label="Dismiss"><IconX /></button>
          <small>Copy it now; it is not shown again.</small>
          <div className="qo-copy"><code>{newKey}</code><button className="btn" onClick={() => void navigator.clipboard?.writeText(newKey)}>Copy</button></div>
        </div>
      )}
      <div className="qo-table-wrap">
        <table className="qo-table">
          <thead><tr><th>Name</th><th>Key</th><th>Last used</th><th /></tr></thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id}>
                <td><strong>{k.name}</strong><small>created {relativeTime(k.createdAt)}</small></td>
                <td><code>{k.prefix}…</code></td>
                <td>{k.lastUsedAt ? relativeTime(k.lastUsedAt) : <span className="qo-muted">never</span>}</td>
                <td className="qo-row-actions"><button className="btn btn-ghost" disabled={busy} onClick={() => void confirmThen({ title: `Revoke ${k.name}?`, body: "Anything using it (a CI workflow) stops working.", confirmLabel: "Revoke" }, () => deleteApiKey(k.id))}>Revoke</button></td>
              </tr>
            ))}
            {keys.length === 0 && <tr><td colSpan={4} className="qo-muted">None.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="qo-add-row">
        <input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="e.g. github-actions" />
        <button className="btn" disabled={busy || !keyName.trim()} onClick={async () => {
          let key = "";
          if (await run(async () => { key = (await createApiKey(keyName.trim())).key; })) { setNewKey(key); setKeyName(""); }
        }}><IconPlus /> Create key</button>
      </div>
      {dialog}
    </section>
  );
}

// ---------------------------------------------------------------- CI

function CiSection({ status, groups, reload, setError }: { status: QaOrchestratorStatus; groups: QaRunnerGroup[]; reload: () => Promise<void>; setError: (e: string | null) => void }) {
  const { busy, run, dialog } = useAction(reload, setError);
  const [yaml, setYaml] = useState<string | null>(null);
  const [key, setKey] = useState<string | null>(null);
  useEffect(() => { getWorkflowYaml().then(setYaml).catch((e) => setError((e as Error).message)); }, [setError]);

  const download = () => {
    if (!yaml) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([yaml], { type: "text/yaml" }));
    a.download = "qa-tests.yml";
    a.click();
  };

  return (
    <section className="qo-section">
      <div className="qo-section-head">
        <div>
          <h3>Test every pull request in CI</h3>
          <p>The workflow builds the pack at the PR's commit, installs that build on a runner and fails the check unless every plan in <code>qa/plans/</code> passes.</p>
        </div>
      </div>
      <ol className="qo-steps">
        <li>
          <strong>Add the workflow</strong> to the pack repository as <code>.github/workflows/qa-tests.yml</code>.
          <div className="qo-actions">
            <button className="btn" disabled={!yaml} onClick={download}>Download qa-tests.yml</button>
            <button className="btn btn-ghost" disabled={!yaml} onClick={() => yaml && void navigator.clipboard?.writeText(yaml)}>Copy</button>
          </div>
        </li>
        <li>
          <strong>Set the repository variable</strong> <code>QA_ORCHESTRATOR_URL</code> (Settings → Secrets and variables → Actions → Variables):
          <div className="qo-copy"><code>{status.publicUrl ?? status.url}</code><button className="btn btn-ghost" onClick={() => void navigator.clipboard?.writeText(status.publicUrl ?? status.url ?? "")}>Copy</button></div>
        </li>
        <li>
          <strong>Set the repository secret</strong> <code>QA_ORCHESTRATOR_KEY</code> to an API key.
          {key ? (
            <div className="qo-copy"><code>{key}</code><button className="btn" onClick={() => void navigator.clipboard?.writeText(key)}>Copy</button></div>
          ) : (
            <div className="qo-actions">
              <button className="btn" disabled={busy} onClick={() => void run(async () => setKey((await createApiKey(`github-actions ${new Date().toISOString().slice(0, 10)}`)).key))}>Create a key for it</button>
              <span className="qo-muted">shown once; one key can serve every repository</span>
            </div>
          )}
        </li>
        <li>
          <strong>Optional.</strong> Tests run where the repository's setting under Repositories says, else on any runner; each plan once, on the least
          busy one. A repository can override that, and the comment, with variables:
          <table className="qo-table qo-vars">
            <tbody>
              <tr><td><code>QA_RUNNER_GROUP</code></td><td>a runner group id{groups.length ? `: ${groups.map((g) => g.id).join(", ")}` : ""}</td></tr>
              <tr><td><code>QA_RUNNERS</code></td><td>runner names, comma separated</td></tr>
              <tr><td><code>QA_PR_COMMENT</code></td><td><code>false</code> to skip the pull request comment</td></tr>
            </tbody>
          </table>
        </li>
      </ol>
      <p className="qo-hint">
        Every pull request then gets a check, and one comment with the results and the failed checks' screenshots, updated on each push and linking to
        the run on the project's QA page, where every run is listed with the pull request and workflow run that started it.
      </p>
      {dialog}
    </section>
  );
}

// ---------------------------------------------------------------- page

export default function AdminQaPage({ onClose, onOpenUsers, onOpenUsage, onOpenErrors }: Props) {
  const [section, setSection] = useState<Section>("runners");
  const [status, setStatus] = useState<QaOrchestratorStatus | null>(null);
  const [computers, setComputers] = useState<QaComputer[]>([]);
  const [groups, setGroups] = useState<QaRunnerGroup[]>([]);
  const [repos, setRepos] = useState<QaGitHubRepo[]>([]);
  const [keys, setKeys] = useState<QaApiKey[]>([]);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const s = await getOrchestratorStatus();
    setStatus(s);
    // Until the orchestrator answers, the connection is the one thing to look at,
    // and it stays open once a save there connects it.
    if (!s.configured || !s.reachable) {
      setSection("connection");
      return;
    }
    const [c, g, r, k] = await Promise.all([listComputers(), listGroups(), listRepos(), listApiKeys()]);
    setComputers(c);
    setGroups(g);
    setRepos(r);
    setKeys(k);
  }, []);

  useEffect(() => {
    reload().catch((e) => setError((e as Error).message));
  }, [reload, section]);
  // Runner states change by themselves.
  const refreshComputers = useCallback(() => listComputers().then(setComputers).catch(() => {}), []);
  usePolling(refreshComputers, 15_000, { enabled: section === "runners" || section === "groups", immediate: false });

  return (
    <div className="members members-screen qo-screen">
      <div className="members-head">
        <div className="members-head-inner">
          <div className="members-title-row">
            <div className="members-title">
              <h2>QA runners</h2>
              <p>
                The qa-agent orchestrator: Windows runners, where each repository's tests run, and CI.
                {status?.configured && <> <span className={`qo-dot ${status.reachable ? "qo-dot-ok" : "qo-dot-bad"}`} /> {status.url}</>}
              </p>
            </div>
            <div className="members-head-actions">
              <button className="btn btn-ghost" onClick={onClose}><IconBack /> Back to chat</button>
            </div>
          </div>
          <div className="admin-tabs" role="tablist" aria-label="Workspace administration">
            <button role="tab" aria-selected="false" onClick={onOpenUsers}>Users</button>
            <button role="tab" aria-selected="false" onClick={onOpenUsage}>Usage</button>
            <button role="tab" aria-selected="false" onClick={onOpenErrors}>Errors</button>
            <button role="tab" aria-selected="true" className="on">QA</button>
          </div>
        </div>
      </div>

      <div className="members-body">
        <div className="members-body-inner">
          {error && (
            <div className="members-alert" role="alert">
              <span>{error}</span>
              <button onClick={() => setError(null)} aria-label="Dismiss"><IconX /></button>
            </div>
          )}

          {!status ? (
            <div className="members-empty">Connecting to the orchestrator…</div>
          ) : !status.configured ? (
            <>
              <div className="qo-card qo-setup">
                <h3>Not connected</h3>
                <p>
                  Enter the orchestrator's URL and its admin token (the orchestrator's <code>ORCHESTRATOR_ADMIN_TOKEN</code>) under Connection below.
                  The daemon's <code>QA_ORCHESTRATOR_URL</code> and <code>QA_ORCHESTRATOR_TOKEN</code> env vars work too.
                </p>
              </div>
              <QaConnectionSection setError={setError} onSaved={reload} />
            </>
          ) : !status.reachable ? (
            <>
              <div className="qo-card qo-setup">
                <h3>The orchestrator is not answering</h3>
                <p>{status.url}: {status.error}</p>
                <button className="btn" onClick={() => void reload().catch((e) => setError((e as Error).message))}>Retry</button>
              </div>
              <QaConnectionSection setError={setError} onSaved={reload} />
            </>
          ) : (
            <>
              <nav className="qo-subnav">
                {SECTIONS.map((s) => (
                  <button key={s.id} className={section === s.id ? "on" : ""} onClick={() => setSection(s.id)}>
                    {s.label}
                    {s.id === "runners" && <span className="qo-count">{computers.length}</span>}
                    {s.id === "groups" && <span className="qo-count">{groups.length}</span>}
                    {s.id === "repos" && <span className="qo-count">{repos.length}</span>}
                  </button>
                ))}
              </nav>
              {section === "runners" && <RunnersSection computers={computers} groups={groups} reload={reload} setError={setError} />}
              {section === "groups" && <GroupsSection computers={computers} groups={groups} reload={reload} setError={setError} />}
              {section === "repos" && <ReposSection repos={repos} computers={computers} groups={groups} projects={status.projects ?? []} reload={reload} setError={setError} />}
              {section === "access" && <AccessSection keys={keys} reload={reload} setError={setError} />}
              {section === "activity" && <QaActivitySection setError={setError} />}
              {section === "settings" && <QaRetentionSection setError={setError} />}
              {section === "notifications" && <QaNotificationsSection setError={setError} />}
              {section === "ci" && <CiSection status={status} groups={groups} reload={reload} setError={setError} />}
              {section === "connection" && <QaConnectionSection setError={setError} onSaved={reload} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
